/**
 * Shared API client: timeout, auth, and consistent error handling.
 * Use for all backend requests so behaviour is professional and predictable.
 */

const DEFAULT_TIMEOUT_MS = 15000;
const BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

/**
 * Fetch with timeout. Rejects with a descriptive error on timeout or network failure.
 * @param {string} url - Full URL or path (if path, BASE_URL is prepended)
 * @param {RequestInit} options - fetch options
 * @param {number} timeoutMs - timeout in ms
 * @returns {Promise<Response>}
 */
export function fetchWithTimeout(url, options = {}, timeoutMs = DEFAULT_TIMEOUT_MS) {
  const fullUrl = url.startsWith('http') ? url : `${BASE_URL.replace(/\/$/, '')}${url.startsWith('/') ? '' : '/'}${url}`;
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  const fetchPromise = fetch(fullUrl, {
    ...options,
    signal: controller.signal,
    headers: {
      ...(options.body && typeof options.body === 'string' && !options.headers?.['Content-Type'] ? { 'Content-Type': 'application/json' } : {}),
      ...options.headers,
    },
  });
  return fetchPromise.finally(() => clearTimeout(timeoutId));
}

/**
 * Get auth header for admin, driver, or client.
 * @param {string} type - 'admin' | 'driver' | 'client'
 * @returns {{ Authorization: string } | {}}
 */
export function getAuthHeader(type = 'admin') {
  const key = type === 'admin' ? 'admin_token' : type === 'driver' ? 'driver_token' : 'session_token';
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem(key) : null;
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

/**
 * Parse error body from API (FastAPI uses { detail: string } or { detail: [...] } for validation).
 * @param {Response} response
 * @returns {Promise<string>} Human-readable error message
 */
export async function parseErrorDetail(response) {
  try {
    const body = await response.json();
    const d = body?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d) && d.length) {
      const first = d[0];
      const msg = first?.msg ?? first?.loc?.join?.('. ');
      return msg ? String(msg) : JSON.stringify(d[0]);
    }
    if (d && typeof d === 'object') return JSON.stringify(d);
    return response.statusText || `Error ${response.status}`;
  } catch {
    return response.statusText || `Error ${response.status}`;
  }
}

/**
 * Perform an API request with timeout and optional auth.
 * On non-2xx, returns { ok: false, error: string, status }.
 * On success, returns { ok: true, data } (parsed JSON).
 * On network/timeout, returns { ok: false, error: string }.
 *
 * @param {string} url - Path (e.g. '/api/auth/admin/login') or full URL
 * @param {RequestInit} options - fetch options (method, body, etc.)
 * @param {{ timeout?: number, auth?: 'admin'|'driver'|'client', parseJson?: boolean }} opts
 * @returns {Promise<{ ok: true, data: any } | { ok: false, error: string, status?: number }>}
 */
export async function apiRequest(url, options = {}, opts = {}) {
  const { timeout = DEFAULT_TIMEOUT_MS, auth, parseJson = true } = opts;
  const headers = {
    ...options.headers,
    ...(auth ? getAuthHeader(auth) : {}),
  };

  try {
    const response = await fetchWithTimeout(url, { ...options, headers }, timeout);
    if (!response.ok) {
      const error = await parseErrorDetail(response);
      return { ok: false, error, status: response.status };
    }
    const data = parseJson ? await response.json() : await response.text();
    return { ok: true, data };
  } catch (e) {
    const message =
      e.name === 'AbortError'
        ? 'Request timed out. Please try again.'
        : e.message || 'Network error. Please check your connection.';
    return { ok: false, error: message };
  }
}

export default { fetchWithTimeout, getAuthHeader, parseErrorDetail, apiRequest };
